import React, { useEffect, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import dayjs, { type Dayjs } from 'dayjs'
import {
  type BaseJurisdiction,
  type CommitteeJurisdiction, type LeadershipOfficeJurisdiction,
  updateCommitteeJurisdiction, updateLeadershipOfficerJurisdiction
} from 'services'
import {
  accordionSummaryStyles,
  cardStyles,
  EditActions,
  LastUpdatedComponent,
  useGlobalMessageHandler,
  MaterialUIPickers
} from 'components/index'
import { formatErrorFieldMessages, type HttpResponse } from 'interfaces'

interface JurisdictionCardProps< T extends BaseJurisdiction > {
  originalJurisdiction: T
  setOriginalJurisdiction: React.Dispatch<React.SetStateAction<T | undefined>>
  loading: boolean
}

export const JurisdictionCard = <T extends BaseJurisdiction> ({
  originalJurisdiction,
  setOriginalJurisdiction,
  loading
}: JurisdictionCardProps<T>): React.ReactElement => {
  const [expanded, setExpanded] = useState(true)
  const [jurisdiction, setJurisdiction] = useState<T>(originalJurisdiction)
  const [disabled, setDisabled] = useState<boolean>(true)
  const [loadingClick, setLoadingClick] = useState(false)
  const [errorFields, setErrorFields] = useState<string[]>([])
  const { showSuccessMessage, showErrorMessage, clearMessages } = useGlobalMessageHandler()

  useEffect(() => {
    setJurisdiction(originalJurisdiction)
  }, [originalJurisdiction.lastUpdated])

  const handleAccordionChange = (): void => {
    setExpanded(!expanded)
  }

  const toggleDisable = (): void => {
    setDisabled(!disabled)
    setErrorFields([])
    clearMessages()
  }

  const handleFieldChange = (field: keyof T, value: string): void => {
    setJurisdiction(prevState => ({
      ...prevState,
      [field]: value
    }))
  }

  const handleDateChange = (newValue: Dayjs | null): void => {
    setJurisdiction(prevState => ({
      ...prevState,
      documentDate: newValue?.format('YYYY-MM-DD') ?? ''
    }))
  }

  const handleCancelClick = (): void => {
    setJurisdiction(originalJurisdiction)
    setDisabled(true)
    setErrorFields([])
    clearMessages()
  }

  const isCommitteeJurisdiction = (jurisdiction: BaseJurisdiction): jurisdiction is CommitteeJurisdiction => {
    return 'committeeId' in jurisdiction
  }
  const isLeadershipJurisdiction = (jurisdiction: BaseJurisdiction): jurisdiction is LeadershipOfficeJurisdiction => {
    return 'legOfcId' in jurisdiction
  }

  const handleSaveClick = async (): Promise<void> => {
    setLoadingClick(true)
    clearMessages()
    setErrorFields([])

    try {
      let response: HttpResponse<T>
      if (isCommitteeJurisdiction(jurisdiction)) {
        response = await updateCommitteeJurisdiction(jurisdiction)
      } else if (isLeadershipJurisdiction(jurisdiction)) {
        response = await updateLeadershipOfficerJurisdiction(jurisdiction)
      } else {
        showErrorMessage('Unsupported jurisdiction type')
        setLoadingClick(false)
        return
      }

      if (response.succeeded && response.result != null) {
        showSuccessMessage('Jurisdiction updated successfully')
        setOriginalJurisdiction(response.result)
        setDisabled(true)
      } else {
        const errorMessage = formatErrorFieldMessages(response.errors)
        showErrorMessage(errorMessage)
        if (response.errors != null) {
          setErrorFields(Object.keys(response.errors))
        }
      }
    } catch (error) {
      showErrorMessage('Failed to update jurisdiction')
    } finally {
      setLoadingClick(false)
    }
  }

  const isError = (field: string): boolean => {
    return errorFields.includes(field)
  }

  return (
    <Card sx={cardStyles}>
      <Accordion expanded={expanded} onChange={handleAccordionChange}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={accordionSummaryStyles}
        >
          <Box display="flex" justifyContent="space-between" width="100%">
            <Typography variant="h6">Jurisdiction</Typography>
            <LastUpdatedComponent value={jurisdiction.lastUpdated ?? undefined} />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <CardContent>
            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <MaterialUIPickers
                    namelabel="Date"
                    disabled={disabled}
                    value={jurisdiction.documentDate != null ? dayjs(jurisdiction.documentDate) : null}
                    onChange={handleDateChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    disabled={disabled}
                    label="Source"
                    variant="outlined"
                    value={jurisdiction.documentSource ?? ''}
                    error={isError('documentSource')}
                    onChange={(e) => { handleFieldChange('documentSource', e.target.value) }}
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                multiline
                rows={6}
                disabled={disabled}
                label="Document"
                variant="outlined"
                value={jurisdiction.document ?? ''}
                error={isError('document')}
                onChange={(e) => { handleFieldChange('document', e.target.value) }}
              />
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'end', mt: 2 }}>
              {loadingClick && <CircularProgress />}
              {!loadingClick && (
                <EditActions
                  entityName="Jurisdiction"
                  editing={!disabled}
                  disabled={loading}
                  savePlacement="left"
                  onEdit={toggleDisable}
                  onCancel={handleCancelClick}
                  onSave={() => { void handleSaveClick() }}
                />
              )}
            </Box>
          </CardContent>
        </AccordionDetails>
      </Accordion>
    </Card>
  )
}
